import { useLocation } from 'react-router-dom';
import {
  AmplifyError,
  AppCtxData,
  AppData,
  AuthQueryParams,
  AuthUser,
  CognitoAttributes,
  IErrorMessage,
  isAwsError,
  SignUpFlow,
  UserType,
  UseSignUpCtx
} from './types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import qs from 'qs';
import { isString, useReactiveVariable } from 'src/helpers';
import { GET_ME_INFO, GET_MS_TEAMS_URL } from './queries';
import { LazyQueryResultTuple, makeVar, useApolloClient, useLazyQuery } from '@apollo/client';
import { Amplify, Auth, Hub } from 'aws-amplify';
import AuthContext, { SignUpFlowContext } from './context';
import { FreeUser, PaidUser } from './index';
import { ApiMeResponse } from 'src/common';
import { StorageKey } from 'src/types/enums';
import { AmplifyConfig } from 'src/config/amplify-config';
import { ReactiveVariable } from '../../helpers/reactiveVariables';

Amplify.configure({
  ...AmplifyConfig,
  mandatorySignIn: true,
  aws_mandatory_sign_in: true,
  authenticationFlowType: 'USER_PASSWORD_AUTH'
});
Auth.configure({
  ...AmplifyConfig
});

// types from aws cognito
type HubCapsule = {
  channel: string;
  payload: HubPayload;
  source: string;
  patternInfo?: string[];
};
type HubPayload = {
  event: string;
  // types from cognito internal lib, will be removed when upgrade amplify to next major version
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  message?: string;
};
type HubCallback = (capsule: HubCapsule) => void;

export const getAuthUser = async (): Promise<AuthUser | Error | null> => {
  try {
    return await Auth.currentAuthenticatedUser();
  } catch (error) {
    // currentAuthenticatedUser throws an Error if not signed in
    return error instanceof Error ? error : null;
  }
};
export const freeUserVar = makeVar<CognitoAttributes | undefined>(undefined);
export function useFreeUser(): ReactiveVariable<CognitoAttributes | undefined> {
  return useReactiveVariable<CognitoAttributes | undefined>(freeUserVar);
}

export const tempCognitoUserVar = makeVar<AuthUser | undefined>(undefined);
export function useTempCognitoUser(): ReactiveVariable<AuthUser | undefined> {
  return useReactiveVariable<AuthUser | undefined>(tempCognitoUserVar);
}

export const useAppCtx = (): AppCtxData => {
  const [result, setResult] = useState<AppData>(() => ({ loading: true }));
  const { cache } = useApolloClient();
  const [loadPaidUser] = useLazyQuery<ApiMeResponse>(GET_ME_INFO);

  const handlePaidUser = useCallback(
    async (data: AuthUser) => {
      const { data: meData } = await loadPaidUser();
      // if (user?.attributes?.email_verified) {
      try {
        if (meData?.getMe.account) {
          if (!meData.getMe.account.company.enabled || !meData.getMe.account.enabled) {
            setResult(() => ({ loading: false, error: new Error(AmplifyError.DisabledUser) }));
            tempCognitoUserVar(undefined);
          } else {
            setResult(() => ({
              authUser: data,
              email: data.attributes.email,
              permission: UserType.Paid,
              user: meData.getMe.account ? new PaidUser(meData.getMe.account) : undefined,
              loading: false
            }));
            tempCognitoUserVar(undefined);
          }
        } else {
          tempCognitoUserVar(data);
        }
      } catch (error) {
        setResult(() => ({ loading: false, error: error instanceof Error ? error : undefined }));
      }
    },
    [loadPaidUser]
  );

  const handleFreeUser = useCallback((data: AuthUser) => {
    setResult(() => ({
      authUser: data,
      email: data.attributes.email,
      permission: data.attributes['custom:userType'],
      user: new FreeUser(data.attributes),
      loading: false
    }));
    freeUserVar(data.attributes);
  }, []);

  /**
   * Hub events like `tokenRefresh` will not give back the user object.
   * This util will be used to get current user after those events.
   */
  const updateUser = useCallback(async (): Promise<void> => {
    const data = await getAuthUser();
    setResult(currentState => ({ ...currentState, loading: true }));
    try {
      // if ((data as AuthUser)?.attributes?.sub) {
      const permission = (data as AuthUser)?.attributes?.['custom:userType'];
      if (permission !== UserType.Free) {
        await handlePaidUser(data as AuthUser);
      } else {
        handleFreeUser(data as AuthUser);
      }
    } catch (error) {
      setResult(() => ({ loading: false, error: error instanceof Error ? error : undefined }));
      freeUserVar(undefined);
    } finally {
      setResult(currentState => ({ ...currentState, loading: false }));
    }
  }, [handleFreeUser, handlePaidUser]);

  const handleAuth: HubCallback = useCallback(
    async ({ payload: { data, event } }) => {
      switch (event) {
        // success events
        case 'signIn':
        case 'signUp':
        case 'autoSignIn': {
          cache.evict({ fieldName: 'getMe' });
          cache.gc();
          freeUserVar(undefined);
          // await handleSetup(data);
          break;
        }
        case 'signOut': {
          setResult(() => ({ loading: false }));
          cache.evict({ fieldName: 'getMe' });
          cache.gc();
          sessionStorage.removeItem(StorageKey.FromLogin);
          freeUserVar(undefined);
          window.location.reload();
          break;
        }
        // failure events
        case 'tokenRefresh_failure':
        case 'signIn_failure': {
          setResult(() => ({ error: data as Error, loading: false }));
          break;
        }
        case 'autoSignIn_failure': {
          // autoSignIn just returns error message. Wrap it to an Error object
          setResult(() => ({ error: new Error(data.message), loading: false }));
          break;
        }
        // events that need another fetch
        case 'tokenRefresh': {
          await updateUser();
          break;
        }
        default: {
          // we do not need to handle other hub events like `configured`.
          break;
        }
      }
    },
    [cache, updateUser]
  );

  Hub.listen('auth', handleAuth);

  useEffect(() => {
    updateUser();
  }, [updateUser]);

  return useMemo(
    () => ({
      ...result,
      setResult,
      handleFreeUser,
      handlePaidUser
    }),
    [handleFreeUser, handlePaidUser, result]
  );
};

export function useApp(): AppCtxData {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('not in AuthContext provider');
  }

  return ctx;
}

export function useSignUpFlow(): UseSignUpCtx {
  const ctx = useContext(SignUpFlowContext);
  if (!ctx) {
    throw new Error('not in SignUpFlowContext provider');
  }

  return ctx;
}

export function useSignUpCtx(): UseSignUpCtx {
  const [step, setStep] = useState(SignUpFlow.CompanyInfo);
  return useMemo(() => [step, setStep], [step]);
}

export function useAwsError(): [IErrorMessage | null, () => void] {
  const { search } = useLocation();

  useEffect(() => {
    if (search.includes(`?${AuthQueryParams.AswError}`)) {
      const qpError = qs.parse(search.replace('?', ''));
      if (isAwsError(qpError)) {
        const errorMessage = getErrorMessage(qpError[AuthQueryParams.AswError]);
        sessionStorage.setItem(StorageKey.AwsError, JSON.stringify(errorMessage));
      }
    }
  }, [search]);

  const clearFn = useCallback(() => {
    sessionStorage.removeItem(StorageKey.AwsError);
  }, []);
  const res = sessionStorage.getItem(StorageKey.AwsError);
  return [isString(res) ? JSON.parse(res) : res, clearFn];
}

const errorRegexp = new RegExp(
  /(for [a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)|( [a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi
);
function getErrorMessage(initialErrorMessage: string): IErrorMessage {
  const matchesInInitErrorMessage = initialErrorMessage.match(errorRegexp);

  const option = matchesInInitErrorMessage && matchesInInitErrorMessage[0];
  const errorMessage = option
    ? initialErrorMessage.replace(option, '').replace(/[!.,]/g, '').trim()
    : initialErrorMessage;
  return { errorMessage, option };
}

interface IMsTeamsRequest {
  email: string;
  password: string;
  state: string;
}
interface IMsTeamsResponse {
  getMicrosoftRedirectUrlForMe: string;
}
export function useMsTeamsUrl(): LazyQueryResultTuple<IMsTeamsResponse, IMsTeamsRequest> {
  return useLazyQuery<IMsTeamsResponse, IMsTeamsRequest>(GET_MS_TEAMS_URL, {
    fetchPolicy: 'no-cache'
  });
}
